import Phaser from 'phaser';
import { HAPTICS, PALETTE, SPRINT } from '../../config';
import { createAthleteView, type AthleteView } from '../../game/AthleteView';
import { AudioEngine } from '../../game/AudioEngine';
import { getDiscipline } from '../../game/data/disciplines';
import { getSkin } from '../../game/data/skins';
import { countryFlag } from '../../game/data/flags';
import { pickOpponents } from '../../game/data/aiRoster';
import { t } from '../../game/i18n';
import type { DisciplineResult, Standing } from '../../game/types';
import { Button } from '../../ui/Button';
import { PowerMeter } from '../../ui/PowerMeter';
import { TapZones } from '../../ui/TapZones';
import { viewport } from '../../ui/viewport';
import { DisciplineScene } from './DisciplineScene';

type Side = 'L' | 'R';
type Phase = 'intro' | 'showcase' | 'spin' | 'flight' | 'between' | 'done';

const PIVOT_X = 150;
const PPM = SPRINT.pixelsPerMeter;
const MAX_SPIN = 14; // rotational charge ceiling
const ANGLE_TARGET = 0.5; // sweet spot on the release sweep
const ATTEMPTS = 3;

/**
 * Discus — alternate L/R taps to wind up SPIN (the athlete turns in the circle), while a
 * release meter sweeps; tap LANCIA to release. Distance = spin speed × release timing. The
 * disc sails out in a long flat arc. AI fill the field.
 */
export class DiscusThrow extends DisciplineScene {
  private phase: Phase = 'intro';
  private rig!: AthleteView;

  private spin = 0;
  private lastSide: Side | null = null;
  private sweep = 0;
  private reach = 55;
  private throwDist = 0;
  private flightT = 0;
  private flightDur = 1;
  private releaseX = 0; private releaseY = 0;
  private disc!: Phaser.GameObjects.Graphics;
  private windAngle = 0;
  private attempt = 1;
  private discBest = 0;
  private aiIndex = 0;
  private aiRig?: AthleteView;
  private playerMarker?: Phaser.GameObjects.GameObject[];

  private W = 0; private H = 0; private groundY = 0; private uiS = 1; private camTarget = 0;
  private aiThrows: { name: string; species: string; nationality: string; metric: number; skin: string }[] = [];

  private spinBar!: PowerMeter;
  private angleBar!: PowerMeter;
  private hud!: Phaser.GameObjects.Text;
  private bigText!: Phaser.GameObjects.Text;
  private throwBtn!: Button;
  private tapZones!: TapZones;

  constructor() { super('DiscusThrow'); }

  create(): void {
    super.create();
    this.phase = 'intro';
    this.spin = 0; this.lastSide = null; this.sweep = 0; this.throwDist = 0; this.flightT = 0; this.camTarget = 0; this.windAngle = 0;
    this.attempt = 1; this.discBest = 0; this.aiIndex = 0; this.playerMarker = undefined;

    const { w, h, s } = viewport(this);
    this.W = w; this.H = h; this.uiS = s;
    this.groundY = h - Math.max(96, h * 0.2);
    this.reach = 32 + (this.athlete.stats.power / 100) * 42;

    const src = this.textures.get('stadium').getSourceImage();
    this.add.image(0, 0, 'stadium').setOrigin(0, 0).setScrollFactor(0).setDepth(-10)
      .setScale(Math.max(w / src.width, (this.groundY + 30) / src.height));
    this.drawField();
    this.rig = createAthleteView(this, PIVOT_X, this.groundY, this.athlete, Phaser.Math.Clamp(h * 0.34, 170, 320));
    this.rig.container.setDepth(6);
    this.disc = this.add.graphics().setDepth(8);
    this.fetchWorldRecord('discus', 'distance', (m) => this.drawGroundRecord(PIVOT_X + m * PPM, this.groundY, this.groundY - Math.max(70, this.H * 0.18), `${m.toFixed(2)}m`));

    const opponents = pickOpponents(getSkin(this.athlete.skin).id, 3);
    this.aiThrows = opponents.map((o) => ({ name: o.name, species: o.species, nationality: o.nationality, skin: o.skin, metric: Math.max(20, 35 + (o.jumpDistance - 5) * 4.5 + (Math.random() * 12 - 6)) }));

    this.buildHud();
    this.controls.on('tapLeft', () => this.onTap('L'));
    this.controls.on('tapRight', () => this.onTap('R'));
    this.controls.on('action', () => this.release());
    this.startCountdown();
  }

  private drawField(): void {
    const endX = PIVOT_X + 80 * PPM;
    const g = this.add.graphics().setDepth(-6);
    g.fillStyle(0x4e8b4e, 1).fillRect(0, this.groundY, endX, this.H - this.groundY);
    g.fillStyle(0xcfcfd6, 1).fillEllipse(PIVOT_X, this.groundY + 6, 78, 24);
    g.lineStyle(3, 0xf4f7ff, 0.9).strokeEllipse(PIVOT_X, this.groundY + 6, 78, 24);
    for (let m = 20; m <= 75; m += 20) {
      const x = PIVOT_X + m * PPM;
      g.fillStyle(0x66a366, 1).fillRect(x, this.groundY, 2, 16);
      this.add.text(x, this.groundY + 20, `${m}m`, { fontFamily: '"Twemoji Country Flags", system-ui', fontSize: '14px', color: '#cfe6cf' }).setOrigin(0.5, 0).setDepth(-6);
    }
    this.cameras.main.setBounds(0, 0, endX + this.W, this.H);
  }

  private buildHud(): void {
    const fs = (n: number) => `${Math.max(13, Math.round(n * this.uiS))}px`;
    this.hud = this.add.text(this.W / 2, 16, t('game.hudSpinThrow'), { fontFamily: '"Twemoji Country Flags", system-ui', fontSize: fs(24), color: '#f4f7ff', fontStyle: 'bold' })
      .setOrigin(0.5, 0).setScrollFactor(0).setShadow(0, 3, '#000000aa', 5);
    const barW = Math.min(280, this.W * 0.42);
    const topY = Math.max(84, this.H * 0.15); // below the top-right quit button
    this.spinBar = new PowerMeter(this, 30 + barW / 2, topY, { width: barW, height: 18, fillColor: PALETTE.cadence });
    this.add.text(30 + barW / 2, topY - 18, t('hud.spins'), { fontFamily: '"Twemoji Country Flags", system-ui', fontSize: fs(12), color: '#d3dbf2' }).setOrigin(0.5).setScrollFactor(0);
    this.angleBar = new PowerMeter(this, this.W - 30 - barW / 2, topY, { width: barW, height: 18, target: [ANGLE_TARGET - 0.12, ANGLE_TARGET + 0.12], fillColor: PALETTE.power });
    this.add.text(this.W - 30 - barW / 2, topY - 18, t('hud.release'), { fontFamily: '"Twemoji Country Flags", system-ui', fontSize: fs(12), color: '#d3dbf2' }).setOrigin(0.5).setScrollFactor(0);
    this.bigText = this.add.text(this.W / 2, this.H * 0.4, '', { fontFamily: '"Twemoji Country Flags", system-ui', fontSize: fs(80), color: '#ffd24a', fontStyle: 'bold' })
      .setOrigin(0.5).setScrollFactor(0).setShadow(0, 8, '#00000088', 12);
    const label = this.sys.game.device.os.desktop ? `🥏 ${t('btn.throw')} ${t('btn.space')}` : `🥏 ${t('btn.throw')}`;
    this.throwBtn = new Button(this, this.W / 2, this.H - Math.max(30, 38 * this.uiS), label, () => this.release(), {
      width: Math.min(200, this.W * 0.4), height: Math.max(52, 58 * this.uiS), fontSize: Math.max(18, Math.round(22 * this.uiS)), color: PALETTE.power,
    });
    this.throwBtn.setScrollFactor(0).setAlpha(0.92).setVisible(false);
    this.tapZones = new TapZones(this);
    this.tapZones.setVisible(false);
  }

  private startCountdown(): void {
    ['3', '2', '1', t('game.go')].forEach((label, i) => {
      this.time.delayedCall(i * 600, () => {
        this.bigText.setText(label).setAlpha(1).setScale(1.5);
        this.tweens.add({ targets: this.bigText, scale: 1, duration: 300, ease: 'Back.out' });
        if (label === t('game.go')) {
          AudioEngine.go();
          this.tweens.add({ targets: this.bigText, alpha: 0, delay: 250, duration: 200 });
          this.runAiShowcase();
        } else AudioEngine.countdownTick();
      });
    });
  }

  /** Each rival's animal winds up a spin and the disc flies to their best, then a flag. */
  private runAiShowcase(): void {
    this.phase = 'showcase';
    if (this.aiIndex >= this.aiThrows.length) { this.rig.container.setVisible(true); this.beginAttempt(); return; }
    this.rig.container.setVisible(false);
    const a = this.aiThrows[this.aiIndex]!;
    this.hud.setText(`🥏 ${a.name}`);
    this.aiRig?.destroy();
    this.aiRig = createAthleteView(this, PIVOT_X, this.groundY, { skin: a.skin }, Phaser.Math.Clamp(this.H * 0.34, 170, 320));
    this.aiRig.container.setDepth(6);
    this.camTarget = Phaser.Math.Clamp(PIVOT_X - this.W * 0.3, 0, this.cameras.main.getBounds().width - this.W);
    const obj = { p: 0 };
    this.tweens.add({
      targets: obj, p: 1, duration: 800, ease: 'Sine.inOut',
      onUpdate: () => this.aiRig!.container.setAngle(Math.sin(obj.p * Math.PI * 4) * 14),
      onComplete: () => { this.aiRig!.container.setAngle(0); this.aiRig!.setPose('celebrate'); this.aiThrowArc(a); },
    });
  }

  private aiThrowArc(a: { name: string; nationality: string; metric: number }): void {
    const landX = PIVOT_X + a.metric * PPM;
    const releaseY = this.groundY - this.H * 0.22;
    this.camTarget = Phaser.Math.Clamp(landX - this.W * 0.5, 0, this.cameras.main.getBounds().width - this.W);
    const obj = { p: 0 };
    this.tweens.add({
      targets: obj, p: 1, duration: 650, ease: 'Sine.in',
      onUpdate: () => {
        const x = Phaser.Math.Linear(PIVOT_X + 20, landX, obj.p);
        const y = (this.groundY - 10) - Math.sin(obj.p * Math.PI) * this.H * 0.34 - (1 - obj.p) * (this.groundY - 10 - releaseY);
        this.drawDisc(x, y, this.time.now / 60);
      },
      onComplete: () => {
        this.disc.clear();
        this.flagMarker(landX, PALETTE.cadence, `${countryFlag(a.nationality)} ${a.name.split(' ')[0]} · ${a.metric.toFixed(2)}m`, false);
        AudioEngine.thud();
        this.tweens.add({ targets: this.aiRig!.container, alpha: 0, duration: 250, onComplete: () => { this.aiRig?.destroy(); this.aiRig = undefined; this.aiIndex++; this.runAiShowcase(); } });
      },
    });
  }

  private beginAttempt(): void {
    this.spin = 0; this.sweep = 0; this.lastSide = null; this.windAngle = 0; this.flightT = 0;
    this.phase = 'spin';
    this.disc.clear();
    this.bigText.setAlpha(0);
    this.rig.container.setVisible(true).setAngle(0).setAlpha(1);
    this.rig.container.x = PIVOT_X;
    this.rig.setPose('idle');
    this.controls.setEnabled(true);
    this.throwBtn.setVisible(true);
    this.tapZones.setVisible(true);
    this.camTarget = Phaser.Math.Clamp(PIVOT_X - this.W * 0.3, 0, this.cameras.main.getBounds().width - this.W);
    this.cameras.main.scrollX = this.camTarget;
    this.hud.setText(t('game.attemptSpin', { n: this.attempt, max: ATTEMPTS }));
  }

  private flagMarker(x: number, color: number, label: string, mine: boolean): Phaser.GameObjects.GameObject[] {
    const poleH = Math.max(46, this.H * 0.11);
    const top = this.groundY - poleH;
    const g = this.add.graphics().setDepth(mine ? 6 : 4);
    g.fillStyle(0xffffff, 0.85).fillRect(x - 1.5, top, 3, poleH);
    g.fillStyle(color, 1).fillTriangle(x + 1.5, top, x + 1.5, top + 18, x + 26, top + 9);
    g.fillStyle(color, 1).fillCircle(x, this.groundY, 4);
    const t = this.add.text(x, top - 6, label, {
      fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${Math.round(12 * this.uiS)}px`, color: '#0b1020', fontStyle: 'bold',
      backgroundColor: mine ? '#ffd24a' : '#bfe6ff', padding: { x: 5, y: 2 },
    }).setOrigin(0.5, 1).setDepth(mine ? 7 : 5);
    return [g, t];
  }

  private onTap(side: Side): void {
    if (this.phase !== 'spin') return;
    const alternated = side !== this.lastSide;
    this.lastSide = side;
    this.spin = Math.min(MAX_SPIN, this.spin + (alternated ? 1.4 : 0.4));
    this.tapZones.flash(side);
    AudioEngine.tap(alternated ? 0.6 : 0.2);
    this.vibrate(HAPTICS.tap);
  }

  private release(): void {
    if (this.phase !== 'spin') return;
    const spinNorm = Phaser.Math.Clamp(this.spin / MAX_SPIN, 0, 1);
    if (spinNorm < 0.18) { this.hud.setText(t('game.tooSlowSpin')); return; }
    const angleQ = Phaser.Math.Clamp(1 - Math.abs(this.sweep - ANGLE_TARGET) / 0.5, 0.2, 1);
    this.throwDist = Math.max(8, spinNorm * this.reach * (0.45 + 0.55 * angleQ));
    this.releaseX = PIVOT_X + 20;
    this.releaseY = this.groundY - this.H * 0.22;
    this.phase = 'flight';
    this.flightT = 0;
    this.flightDur = Phaser.Math.Clamp(this.throwDist / 32, 0.8, 2.1);
    this.controls.setEnabled(false);
    this.throwBtn.setVisible(false);
    this.tapZones.setVisible(false);
    this.rig.container.setAngle(0);
    this.rig.setPose('celebrate');
    AudioEngine.whoosh();
    this.vibrate(HAPTICS.goodTap);
  }

  update(_t: number, delta: number): void {
    const dt = delta / 1000;
    this.cameras.main.scrollX = Phaser.Math.Linear(this.cameras.main.scrollX, this.camTarget, 0.12);
    this.rig.update(dt);
    if (this.phase === 'spin') { this.updateSpin(dt); return; }
    if (this.phase === 'flight') { this.updateFlight(dt); }
  }

  private updateSpin(dt: number): void {
    this.spin = Math.max(0, this.spin - dt * 1.5); // bleeds without taps
    const spinNorm = Phaser.Math.Clamp(this.spin / MAX_SPIN, 0, 1);
    this.spinBar.set(spinNorm);
    // The athlete winds back and forth, faster as spin builds.
    this.windAngle = Math.sin(this.time.now / 1000 * (3 + spinNorm * 6)) * (4 + spinNorm * 10);
    this.rig.container.setAngle(this.windAngle);
    // Release meter sweeps; faster spin = faster sweep (harder timing, bigger reward).
    this.sweep = 0.5 + 0.5 * Math.sin(this.time.now / 1000 * (2.4 + spinNorm * 1.6));
    this.angleBar.set(this.sweep, this.sweep);
    this.camTarget = Phaser.Math.Clamp(PIVOT_X - this.W * 0.3, 0, this.cameras.main.getBounds().width - this.W);
  }

  private drawDisc(x: number, y: number, tilt: number): void {
    const g = this.disc;
    g.clear();
    g.fillStyle(0xd9a441, 1).fillEllipse(x, y, 26, Math.max(5, 26 * Math.abs(Math.cos(tilt))));
    g.fillStyle(0xb9842c, 1).fillEllipse(x, y, 14, Math.max(3, 14 * Math.abs(Math.cos(tilt))));
  }

  private updateFlight(dt: number): void {
    this.flightT += dt;
    const p = Phaser.Math.Clamp(this.flightT / this.flightDur, 0, 1);
    const landX = PIVOT_X + this.throwDist * PPM;
    const x = Phaser.Math.Linear(this.releaseX, landX, p);
    const peak = this.H * 0.34;
    const y = (this.groundY - 10) - Math.sin(p * Math.PI) * peak - (1 - p) * (this.groundY - 10 - this.releaseY);
    this.drawDisc(x, y, this.time.now / 60); // fast spin tilt
    this.camTarget = Phaser.Math.Clamp(x - this.W * 0.45, 0, this.cameras.main.getBounds().width - this.W);
    if (p >= 1) this.land();
  }

  private land(): void {
    this.phase = 'between';
    const landX = PIVOT_X + this.throwDist * PPM;
    this.cameras.main.shake(140, 0.005);
    AudioEngine.thud();
    this.vibrate(HAPTICS.finish);
    const dust = this.add.particles(landX, this.groundY, 'spark', {
      lifespan: 520, speed: { min: 30, max: 130 }, angle: { min: 200, max: 340 },
      scale: { start: 0.5, end: 0 }, alpha: { start: 0.6, end: 0 }, tint: 0x9a9a4e, gravityY: 320, emitting: false,
    });
    dust.explode(14);
    this.time.delayedCall(800, () => dust.destroy());

    const isBest = this.throwDist > this.discBest;
    if (isBest) {
      this.discBest = this.throwDist;
      this.playerMarker?.forEach((o) => o.destroy());
      this.playerMarker = this.flagMarker(landX, PALETTE.sun, `${countryFlag(this.athlete.nationality)} ${this.athlete.name.split(' ')[0]} · ${this.discBest.toFixed(2)}m`, true);
    }
    if (isBest) AudioEngine.fanfare();
    this.bigText.setText(`${this.throwDist.toFixed(2)} m${isBest ? '  ⭐' : ''}`).setColor('#ffd24a').setAlpha(1).setScale(0.6);
    this.tweens.add({ targets: this.bigText, scale: 1, duration: 450, ease: 'Back.out' });
    this.hud.setText(t('game.attemptBest', { n: this.attempt, max: ATTEMPTS, m: this.discBest.toFixed(2) }));

    if (this.attempt < ATTEMPTS) {
      this.attempt++;
      this.time.delayedCall(1700, () => this.beginAttempt());
    } else {
      this.time.delayedCall(1900, () => this.finalize());
    }
  }

  private finalize(): void {
    this.phase = 'done';
    const standings: Standing[] = [
      { name: this.athlete.name, species: this.athlete.species, isPlayer: true, nationality: this.athlete.nationality, metric: this.discBest },
      ...this.aiThrows.map((a) => ({ name: a.name, species: a.species, isPlayer: false, nationality: a.nationality, metric: a.metric })),
    ].sort((x, y) => y.metric - x.metric);
    const rank = standings.findIndex((s) => s.isPlayer) + 1;
    const score = Math.round(Phaser.Math.Clamp(this.discBest * 14, 0, 1000));
    const reward = getDiscipline('discus').scoreToReward(score);
    const result: DisciplineResult = {
      disciplineId: 'discus', metricKind: 'distance', athleteId: this.athlete.id, athleteName: this.athlete.name,
      species: this.athlete.species, nationality: this.athlete.nationality, metric: this.discBest, score, reward, rank, standings,
    };
    void this.finishDiscipline(result);
  }

  private vibrate(pattern: number | number[]): void {
    if (typeof navigator !== 'undefined' && 'vibrate' in navigator) {
      try { navigator.vibrate(pattern); } catch { /* unsupported */ }
    }
  }
}
